import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { linkTo, getImageUrl } from "@/lib/utils";
import StickyNavigation from "@/components/layout/StickyNavigation";
import TenantFooter from "@/components/layout/TenantFooter";
import { supabase, isSupabaseReady } from "@/integrations/supabase/client";
import { getPageTitle } from "@/utils/PageUtils";

// Minimal Markdown -> HTML fallback to handle headings, bold, italics, links, lists and paragraphs
function markdownToHtml(md: string | null | undefined): string {
  if (!md) return "";
  let s = md.replace(/\r\n?/g, "\n");

  // Escape HTML
  s = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Headings (## and ###)
  s = s.replace(/^###\s+(.+)$/gm, "<h3>$1</h3>");
  s = s.replace(/^##\s+(.+)$/gm, "<h2>$1</h2>");

  // Bold / italics
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__(.+?)__/g, "<strong>$1</strong>");
  s = s.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  s = s.replace(/_(.+?)_/g, "<em>$1</em>");

  // Links
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "<a href=\"$2\" target=\"_blank\" rel=\"noopener noreferrer\">$1</a>");

  // Lists
  s = s.replace(/^(?:- |\* )(.*)$/gm, "<li>$1</li>");
  s = s.replace(/(<li>.*<\/li>)(?:\n(?=<li>)|$)/g, "<ul>$1</ul>");

  // Paragraphs (wrap blocks not already HTML tags)
  const blocks = s.split(/\n\s*\n/).map((b) => {
    const t = b.trim();
    if (!t) return "";
    if (/^<\/?(h2|h3|ul|li|p|blockquote)/i.test(t)) return t;
    return `<p>${t.replace(/\n/g, "<br />")}</p>`;
  });

  return blocks.filter(Boolean).join("\n");
}

// Normalize plain HTML content to preserve line spacing.
// If no block tags are present, convert double newlines to <p> and single newlines to <br />.
function normalizeContentHtml(html: string | null | undefined): string {
  if (!html) return "";
  const s = html.replace(/\r\n?/g, "\n").trim();

  // If it already contains block-level tags, return as-is
  if (/<(?:p|h[1-6]|ul|ol|li|blockquote|pre|table|hr|br)\b/i.test(s)) {
    return s;
  }

  const blocks = s
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((b) => `<p>${b.replace(/\n/g, "<br />")}</p>`);

  return blocks.join("\n");
}

type RelatedPost = {
  slug: string;
  title: string;
  cover: string;
  author?: string | null;
  date?: string | null;
};

export default function BlogPostPage() {
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState<string>("Post Title Placeholder");
  const [cover, setCover] = useState<string>(getImageUrl("placeholder.svg"));
  const [author, setAuthor] = useState<string>("YC Tech Team");
  const [date, setDate] = useState<string>(new Date().toISOString());
  const [contentHtml, setContentHtml] = useState<string>("");
  const [contentMd, setContentMd] = useState<string>("");
  const [section, setSection] = useState<string | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  const reading = useMemo(() => {
    const source = contentMd || contentHtml.replace(/<[^>]+>/g, " ");
    const words = source ? source.trim().split(/\s+/).length : 0;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  }, [contentHtml, contentMd]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      if (!slug || !isSupabaseReady) {
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from("blog_posts")
        .select(
          "title,slug,excerpt,content_html,content_md,cover_image_url,tags,author_name,section,published_at,status"
        )
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (!ignore && data) {
        setTitle(data.title || "Post Title Placeholder");
        setCover(data.cover_image_url || getImageUrl("placeholder.svg"));
        setAuthor(data.author_name || "YC Tech Team");
        setDate(data.published_at || new Date().toISOString());
        setContentHtml(data.content_html || "");
        setContentMd(data.content_md || "");
        setTags((data as any).tags || []);
        setSection((data as any).section || "Business");
      }

      if (!ignore) {
        setLoading(false);
      }
    }

    load();

    return () => {
      ignore = true;
    };
  }, [slug]);

  useEffect(() => {
    let ignore = false;
    document.title = getPageTitle(title);

    async function loadRelated() {
      if (!slug || !section) return;

      try {
        const seen = new Set<string>([slug]);
        const picks: RelatedPost[] = [];

        const take = (arr: any[], max: number) => {
          for (const d of arr) {
            if (picks.length >= max) break;
            if (!d?.slug || seen.has(d.slug)) continue;
            seen.add(d.slug);
            picks.push({
              slug: d.slug,
              title: d.title,
              cover: d.cover_image_url || getImageUrl("placeholder.svg"),
              author: d.author_name || "YC Tech Team",
              date: d.published_at || null,
            });
          }
        };

        // 1) Prefer posts that share at least one tag
        if (tags && tags.length) {
          const { data: byTags } = await supabase
            .from("blog_posts")
            .select(
              "slug,title,cover_image_url,status,tags,author_name,section,published_at"
            )
            .eq("status", "published")
            .neq("slug", slug)
            .eq("section", section)
            .overlaps("tags", tags as any)
            .limit(50);

          if (Array.isArray(byTags) && byTags.length) {
            const shuffled = [...byTags].sort(() => Math.random() - 0.5);
            take(shuffled, 5);
          }
        }

        // 2) Fill the remainder randomly from other published posts
        if (picks.length < 5) {
          const { data: others } = await supabase
            .from("blog_posts")
            .select(
              "slug,title,cover_image_url,status,author_name,section,published_at"
            )
            .eq("status", "published")
            .neq("slug", slug)
            .eq("section", section)
            .limit(50);

          if (Array.isArray(others) && others.length) {
            const shuffled = [...others].sort(() => Math.random() - 0.5);
            take(shuffled, 5);
          }
        }

        if (!ignore) {
          setRelated(picks);
        }
      } catch {
        // ignore related errors
      }
    }

    loadRelated();

    return () => {
      ignore = true;
    };
  }, [slug, section, tags.join(",")]);

  const contentToRender =
    contentHtml && contentHtml.trim().length > 0
      ? normalizeContentHtml(contentHtml)
      : markdownToHtml(contentMd);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <StickyNavigation />

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="max-w-3xl">
            <Link
              to={linkTo("/chronicle")}
              className="text-sm text-gray-600 hover:text-blue-700"
            >
              Back to The Chronicle
            </Link>
            <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
              {title}
            </h1>
            <div className="mt-3 text-sm text-gray-600 flex gap-2 flex-wrap items-center">
              <span>{author}</span>
              <span>|</span>
              <span>{new Date(date).toLocaleDateString()}</span>
              <span>|</span>
              <span>{reading}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Cover image */}
      <div className="w-full bg-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="aspect-[16/9] bg-gray-200 overflow-hidden rounded-lg">
            <img
              src={cover}
              alt={title}
              className="h-full w-full object-cover"
              fetchPriority="high"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                img.onerror = null; // prevent loop
                img.src = getImageUrl("placeholder.svg");
                img.style.display = "block";
              }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {loading && !contentToRender ? (
            <div className="text-sm text-gray-500">Loading article...</div>
          ) : (
            <div className="prose prose-gray max-w-none prose-headings:mt-6 prose-headings:mb-3 prose-p:my-3">
              <div
                dangerouslySetInnerHTML={{
                  __html: contentToRender,
                }}
              />
            </div>
          )}
        </div>

        {/* Related articles */}
        <div className="mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Related articles
            </h2>
            {related.length === 0 ? (
              <div className="text-sm text-gray-500">
                No related articles yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={linkTo(`/chronicle/${p.slug}`)}
                    className="group block rounded-lg border overflow-hidden hover:shadow-sm"
                  >
                    <div className="aspect-[3/2] bg-gray-100 overflow-hidden">
                      <img
                        src={p.cover}
                        alt={p.title}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                      />
                    </div>
                    <div className="p-3 h-24 text-sm lg:text-base text-gray-900 group-hover:text-blue-700 flex flex-col justify-between items-start text-left">
                      <div className="font-medium leading-snug line-clamp-2">
                        {p.title}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {p.author || "YC Tech Team"}
                        {p.date
                          ? ` | ${new Date(p.date).toLocaleDateString()}`
                          : ""}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <TenantFooter />
    </div>
  );
}
